import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Gift, ArrowRight, Storefront, Heart,
  Lightning, Star, Crown
} from '@phosphor-icons/react';
import GlobalNav from '../../components/GlobalNav';
import { useLanguage } from '../../contexts/LanguageContext';

const GiftCards = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const amounts = [10, 25, 50, 75, 100, 150, 200];

  const features = [
    {
      icon: Storefront,
      title: t('giftcards_feature_1'),
      description: t('giftcards_feature_1_desc')
    },
    {
      icon: Lightning,
      title: t('giftcards_feature_2'),
      description: t('giftcards_feature_2_desc')
    },
    {
      icon: Star,
      title: t('giftcards_feature_3'),
      description: t('giftcards_feature_3_desc')
    },
    {
      icon: Crown,
      title: t('giftcards_feature_4'),
      description: t('giftcards_feature_4_desc')
    }
  ];

  const steps = [
    { num: '1', text: t('giftcards_step_1') },
    { num: '2', text: t('giftcards_step_2') },
    { num: '3', text: t('giftcards_step_3') },
    { num: '4', text: t('giftcards_step_4') }
  ];

  const occasions = [
    t('giftcards_occasion_1'),
    t('giftcards_occasion_2'),
    t('giftcards_occasion_3'),
    t('giftcards_occasion_4'),
    t('giftcards_occasion_5'),
    t('giftcards_occasion_6'),
    t('giftcards_occasion_7')
  ];

  return (
    <div className="min-h-screen bg-[#0F1A14] text-white">
      <GlobalNav />

      {/* Hero */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 bg-[#C3A365]/10 border border-[#C3A365]/30 px-4 py-2 rounded-full mb-8"
          >
            <Gift size={16} className="text-[#C3A365]" weight="fill" />
            <span className="text-sm text-[#C3A365] font-medium">{t('giftcards_badge')}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-5xl sm:text-6xl font-light tracking-tight mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            {t('giftcards_title_1')}<br />
            <span className="text-[#C3A365]">{t('giftcards_title_2')}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-[#8BA89A] max-w-2xl mx-auto"
          >
            {t('giftcards_subtitle')}
          </motion.p>
        </div>
      </section>

      {/* Gift Card Preview */}
      <section className="px-6 pb-24">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            {/* Card Visual */}
            <div className="aspect-[1.6/1] max-w-md mx-auto relative">
              <div className="absolute inset-0 bg-gradient-to-br from-[#145A3A] via-[#1A734A] to-[#145A3A] rounded-2xl shadow-[0_20px_60px_rgba(20,90,58,0.4)] overflow-hidden">
                {/* Pattern */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute inset-0" style={{
                    backgroundImage: 'radial-gradient(circle at 2px 2px, #C3A365 1px, transparent 0)',
                    backgroundSize: '24px 24px'
                  }} />
                </div>

                {/* Content */}
                <div className="absolute inset-0 p-8 flex flex-col justify-between">
                  <div className="flex justify-between items-start">
                    <div>
                      <span className="brand-logo text-2xl text-white/90">habitué</span>
                      <p className="text-white/50 text-xs mt-1">{t('gift_card_label')}</p>
                    </div>
                    <Gift size={32} className="text-[#C3A365]/50" weight="duotone" />
                  </div>
                  <div>
                    <p className="text-white/50 text-xs mb-1">{t('gift_value')}</p>
                    <p className="text-4xl text-white font-light">£10 – £200</p>
                    <p className="text-white/40 text-xs mt-1">{t('giftcards_feature_3')}</p>
                  </div>
                </div>

                {/* Golden accent line */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#C3A365] to-transparent" />
              </div>

              {/* Glow */}
              <div className="absolute -inset-8 bg-[#C3A365]/10 blur-3xl rounded-full -z-10" />
            </div>
          </motion.div>

          {/* Amount Selection Preview */}
          <div className="mt-16 text-center">
            <p className="text-[#8BA89A] text-sm mb-6">{t('giftcards_amounts')}</p>
            <div className="flex flex-wrap justify-center gap-3">
              {amounts.map((amount, i) => (
                <motion.div
                  key={amount}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: i * 0.05 }}
                  viewport={{ once: true }}
                  className="w-20 h-20 bg-[#1E2F26] border border-[#283D32] flex items-center justify-center hover:border-[#C3A365]/50 transition-colors cursor-pointer"
                  data-testid={`giftcard-amount-${amount}`}
                >
                  <span className="text-lg text-white">£{amount}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="px-6 py-24 bg-[#18261E]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#C3A365] text-sm tracking-[0.2em] uppercase font-semibold mb-4">
              {t('giftcards_how_badge')}
            </p>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {t('giftcards_how_title')}
            </h2>
          </div>

          <div className="grid sm:grid-cols-4 gap-6">
            {steps.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-14 h-14 rounded-full bg-[#C3A365]/10 border border-[#C3A365]/30 flex items-center justify-center mx-auto mb-4">
                  <span className="text-[#C3A365] text-xl font-light">{step.num}</span>
                </div>
                <p className="text-white text-sm">{step.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="px-6 py-24 bg-[#0F1A14]">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, i) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  viewport={{ once: true }}
                  className="p-6 bg-[#1E2F26]/50 border border-[#283D32]"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#C3A365]/10 flex items-center justify-center mb-4">
                    <Icon size={24} className="text-[#C3A365]" weight="duotone" />
                  </div>
                  <h3 className="text-lg font-medium text-white mb-2">{feature.title}</h3>
                  <p className="text-[#8BA89A] text-sm">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Occasions */}
      <section className="px-6 py-24 bg-[#18261E]">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[#C3A365] text-sm tracking-[0.2em] uppercase font-semibold mb-4">
            {t('giftcards_occasions_badge')}
          </p>
          <h2 className="text-3xl sm:text-4xl font-light tracking-tight mb-8" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {t('giftcards_occasions_title')}
          </h2>

          <div className="flex flex-wrap justify-center gap-3">
            {occasions.map((occasion, i) => (
              <motion.span
                key={occasion}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.05 }}
                viewport={{ once: true }}
                className="px-4 py-2 bg-[#1E2F26] border border-[#283D32] text-[#8BA89A] text-sm"
              >
                {occasion}
              </motion.span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 py-24 bg-gradient-to-b from-[#0F1A14] to-[#18261E]">
        <div className="max-w-3xl mx-auto text-center">
          <Heart size={48} className="text-[#C3A365] mx-auto mb-6" weight="duotone" />
          <h2 className="text-3xl sm:text-4xl font-light tracking-tight mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {t('giftcards_cta_title')}
          </h2>
          <p className="text-[#8BA89A] mb-8">
            {t('giftcards_cta_desc')}
          </p>
          <motion.button
            onClick={() => navigate('/offrir')}
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#C3A365] text-[#0F1A14] font-semibold hover:bg-[#D4B576] transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            data-testid="giftcards-cta-btn"
          >
            <Gift size={20} />
            {t('giftcards_cta_btn')}
          </motion.button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-[#1E2F26]">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <span className="brand-logo text-xl text-[#8BA89A]">habitué</span>
          <p className="text-xs text-[#8BA89A]">© 2025 habitué. {t('footer_rights')}</p>
        </div>
      </footer>
    </div>
  );
};

export default GiftCards;
